import React, { useState, useEffect } from 'react';
import SuggestedQuestions from './components/SuggestedQuestions';
import QuestionInput from './components/QuestionInput';
import AnswerArticle from './components/AnswerArticle';
import AuthModal from './components/AuthModal';
import { supabase } from './supabase';
import { API_BASE_URL } from './config';

function App() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Check for existing user on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('whatifai_user');
    if (savedUser) {
      setUser(savedUser);
      // Fetch user's credits from database
      fetchUserCredits(savedUser);
    }
  }, []);

  const fetchUserCredits = async (userEmail) => {
    try {
      const { data, error } = await supabase
        .from('whatifai')
        .select('credits')
        .eq('user', userEmail)
        .single();

      if (error) {
        console.error('Error fetching user credits:', error);
        return;
      }

      setCredits(data.credits || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  const handleQuestionSubmit = async (submittedQuestion) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Check if user has enough credits for article generation
    if (credits < 30) {
      setError('Not enough credits. You need 30 credits to generate an article.');
      return;
    }

    setIsLoading(true);
    setError('');
    setQuestion(submittedQuestion);
    setAnswer('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: submittedQuestion, userEmail: user }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get answer');
      }

      // Deduct credits for article generation
      await deductCredits(30);
      setAnswer(data.answer);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setQuestion('');
    setAnswer('');
    setError('');
  };

  const handleAuthSuccess = (email, userCredits) => {
    setUser(email);
    setCredits(userCredits);
  };

  const handleSignOut = () => {
    localStorage.removeItem('whatifai_user');
    setUser(null);
    setCredits(0);
    setQuestion('');
    setAnswer('');
  };

  const deductCredits = async (amount) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/deduct-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userEmail: user, amount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to deduct credits');
      }

      setCredits(data.newCredits);
    } catch (error) {
      console.error('Error deducting credits:', error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-yellow-50 text-black">
      <div className="max-w-article mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-between items-center mb-8">
            <div></div>
            <h1 className="text-4xl font-mono font-bold">
              What if AI
            </h1>
            <div className="text-right">
              {user ? (
                <div className="space-y-2">
                  <div className="text-sm text-muted font-mono">
                    {user}
                  </div>
                  <div className="text-sm text-muted font-mono">
                    <span className="text-lg">⎊</span> {credits} credits
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="plain-link text-xs"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="plain-link text-sm"
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
          <p className="text-lg text-muted font-mono">
            Get long, ridiculous, yet scientific answers to absurd questions.
          </p>
        </div>

        {/* Main Content */}
        {!answer && !isLoading ? (
          <div className="space-y-12">
            {/* Suggested Questions */}
            <SuggestedQuestions 
              onQuestionClick={handleQuestionSubmit}
              user={user}
              onAuthRequired={() => setShowAuthModal(true)}
              credits={credits}
              onDeductCredits={deductCredits}
            />
            
            {/* Question Input */}
            <QuestionInput 
              onSubmit={handleQuestionSubmit}
              isLoading={isLoading}
              error={error}
            />
          </div>
        ) : (
          /* Answer Article View or Loading */
          <AnswerArticle 
            question={question}
            answer={answer}
            isLoading={isLoading}
            onReset={handleReset}
          />
        )}

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      </div>
    </div>
  );
}

export default App; 